import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { CalendarClock, MapPin, Phone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

export default function Visits() {
  const { t } = useI18n();
  const [visits, setVisits] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(undefined);

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      params.date = `${year}-${month}-${day}`;
    }
    const { data } = await api.get("/visits", { params });
    setVisits(data);
  }, [statusFilter, selectedDate]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/visits/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-visits">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Opérations</div>
        <h1 className="mt-2 font-display text-4xl text-brand-navy tracking-tight">{t("admin.visits")}</h1>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <Card className="overflow-hidden">
            <CardContent className="p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="w-full rounded-xl bg-slate-50/70"
              />
              {selectedDate && (
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setSelectedDate(undefined)} data-testid="clear-date">
                  Effacer date
                </Button>
              )}
            </CardContent>
          </Card>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Statut</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="visit-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="min-w-0">
          <Card className="overflow-hidden">
            <Table data-testid="visits-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Prospect</TableHead>
                  <TableHead>Annonce</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Frais</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visits.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-12">Aucune visite trouvée.</TableCell></TableRow>
                ) : visits.map((v) => (
                  <TableRow key={v.id} data-testid={`visit-row-${v.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm font-semibold text-brand-navy"><CalendarClock className="h-3.5 w-3.5 text-brand-orange" />{v.date}</div>
                      <div className="text-xs text-slate-500">{v.time}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-semibold text-brand-navy">{v.lead?.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{v.lead?.phone}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-brand-navy truncate max-w-xs">{v.listing?.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{v.listing?.category}</div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${v.visit_type === "virtual" ? "bg-cyan-50 text-cyan-700" : "bg-slate-100 text-slate-700"}`}>{v.visit_type}</span>
                      {v.zone && <div className="text-xs text-slate-500 mt-1">{v.zone}</div>}
                    </TableCell>
                    <TableCell><span className="font-bold text-brand-navy">${v.total_fee}</span></TableCell>
                    <TableCell>
                      <Select value={v.status} onValueChange={(s) => changeStatus(v.id, s)}>
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid={`visit-status-${v.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  );
}
